import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injector, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  CryptoDepositInfoDto,
  DepositControllerService,
  FiatDepositInstructionControllerService,
  FiatDepositInstructionDto,
} from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

export const useDeposits = () =>
  signalStoreFeature(
    {
      props: {
        _activeAccountId: type<Signal<number | undefined>>(),
        _injector: type<Injector>(),
        _dialogService: type<MatDialog>(),
      },
    },
    withState({
      depositDetails: new Array<CryptoDepositInfoDto>(),
      fiatDepositDetails: undefined as FiatDepositInstructionDto[] | undefined,
    }),
    withProps(() => ({
      _depositService: inject(DepositControllerService),
      _fiatDepositService: inject(FiatDepositInstructionControllerService),
    })),
    withMethods(store => {
      const loadDepositDetails = rxMethod<string>(
        pipe(
          switchMap(assetCode =>
            store._depositService
              .getDepositInfo(assetCode, store._activeAccountId())
              .pipe(
                tapResponse({
                  next: (depositDetails: CryptoDepositInfoDto[]) =>
                    patchState(store, { depositDetails }),
                  error: (_error: HttpErrorResponse) => undefined,
                }),
              ),
          ),
        ),
      );

      const loadFiatDepositDetails = rxMethod<string>(
        pipe(
          switchMap(assetCode => {
            patchState(store, { fiatDepositDetails: undefined });

            return store._fiatDepositService
              .getLinkedFiatDepositInstruction(assetCode, store._activeAccountId())
              .pipe(
                tapResponse({
                  next: (fiatDepositDetails: FiatDepositInstructionDto[]) =>
                    patchState(store, { fiatDepositDetails }),
                  error: (_error: HttpErrorResponse) => undefined,
                }),
              );
          }),
        ),
      );

      const openDepositDialog = async () => {
        const { DepositDialogComponent } =
          await import('app/dashboard/components/deposit-dialog/deposit-dialog.component');

        store._dialogService.open(DepositDialogComponent, { injector: store._injector });
      };

      return { loadDepositDetails, loadFiatDepositDetails, openDepositDialog };
    }),
  );
